import { useAccount } from 'wagmi';
import { useChainId } from 'wagmi';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { readContract } from '@wagmi/core';
import { nearestUsableTick, TickMath } from '@uniswap/v3-sdk';
import { Token } from '@uniswap/sdk-core';
import { PositionInfoLibrary } from '@/utils/positionInfoLibrary';
import { wagmiConfig } from '@/onchain/OnchainProviders';
import { stateViewAbi, verifiedPoolsPositionManagerAbi } from '@generated/generated';
import { stateViewAddress } from '@generated/generated';
import { getPoolId } from '@/onchain/pools';
import { getPositionUncollectedFees } from '@/utils/uniswapUtils';
import { assetByAddress, getCurrencyAmount } from '@/onchain/assets';
import { Bound, PositionInfo } from '@/utils/types';
import { getPositionFromPositionInfo } from '@/onchain/position';
import { AssetUSDPriceMap, useAssetUSDPrices } from '@/onchain/hooks/useAssetUSDPrice';
import { tickToPriceScaled } from '@/utils/tickMath';
import { getAllPositionManagerAddresses } from '@/onchain/utils/hookConfig';

type FetchedPositionDetails = Awaited<ReturnType<typeof fetchPositionDetails>>;

type PositionDetails = ReturnType<typeof updatePositionDetailsPricing>;

/**
 * Fetch the pool and position info for a given position token id
 * @param chainId - The chain id of the position
 * @param positionTokenId - The position token ID
 * @param positionManagerAddress - Optional: if provided, queries this PM directly for efficiency
 *
 * @returns The pool and position info
 */
export async function fetchPositionDetails(
  chainId: number,
  positionTokenId: bigint,
  positionManagerAddress?: `0x${string}`,
) {
  let poolKey;
  let positionInfoUint256;
  let resolvedPositionManagerAddress;

  // If we know which position manager owns this position, query it directly
  if (positionManagerAddress) {
    try {
      const result = await readContract(wagmiConfig, {
        address: positionManagerAddress,
        abi: verifiedPoolsPositionManagerAbi,
        functionName: 'getPoolAndPositionInfo' as const,
        args: [positionTokenId],
      });
      poolKey = result[0];
      positionInfoUint256 = result[1];
      resolvedPositionManagerAddress = positionManagerAddress;
    } catch (error) {
      throw new Error(
        `Position ${positionTokenId} not found in position manager ${positionManagerAddress}`,
      );
    }
  } else {
    // If position manager is unknown, try all managers (V2 first, then V1)
    const positionManagerAddresses = getAllPositionManagerAddresses(chainId);

    // Try each position manager until we find the position
    for (const address of positionManagerAddresses) {
      try {
        const result = await readContract(wagmiConfig, {
          address,
          abi: verifiedPoolsPositionManagerAbi,
          functionName: 'getPoolAndPositionInfo' as const,
          args: [positionTokenId],
        });

        // Check if the position was actually found - some position managers return zeros
        // instead of reverting when a position doesn't exist
        // Note: We check currency1 instead of currency0 because native ETH pools have currency0=0x0
        const resultPoolKey = result[0];
        const isValidResult =
          resultPoolKey && resultPoolKey.currency1 !== '0x0000000000000000000000000000000000000000';

        if (isValidResult) {
          poolKey = resultPoolKey;
          positionInfoUint256 = result[1];
          resolvedPositionManagerAddress = address;
          break; // Found the position, exit loop
        }
        // Position not found in this manager (returned zeros), try next one
      } catch (error) {
        // Position not found in this manager, try next one
        continue;
      }
    }

    // If position wasn't found in any manager, throw error
    if (!poolKey || !positionInfoUint256 || !resolvedPositionManagerAddress) {
      throw new Error(`Position ${positionTokenId} not found in any position manager`);
    }
  }

  // Get the pool ID from the pool key for onchain queries
  const poolIdBytes32 = getPoolId(poolKey);

  // Parse the position info to get the tickLower and tickUpper
  const positionInfoLib = new PositionInfoLibrary(positionInfoUint256);
  const tickLower = positionInfoLib.getTickLower();
  const tickUpper = positionInfoLib.getTickUpper();

  // Get the fee percentage relative to the fee basis points in decimal percentage representation
  const feePercent = poolKey.fee / 10_000 / 100;

  // Fetch the liquidity, feeGrowthInside0LastX128, and feeGrowthInside1LastX128 from the state view
  const tokenIdBytes32: `0x${string}` = `0x${positionTokenId.toString(16).padStart(64, '0')}`;
  const [liquidity, feeGrowthInside0LastX128, feeGrowthInside1LastX128] = await readContract(
    wagmiConfig,
    {
      address: stateViewAddress[chainId as keyof typeof stateViewAddress],
      abi: stateViewAbi,
      functionName: 'getPositionInfo' as const,
      args: [
        poolIdBytes32,
        resolvedPositionManagerAddress, // Use the detected position manager address
        tickLower,
        tickUpper,
        tokenIdBytes32,
      ],
    },
  );

  // Fetch the feeGrowthInside0X128 and feeGrowthInside1X128 from the state view
  const [feeGrowthInside0X128, feeGrowthInside1X128] = await readContract(wagmiConfig, {
    address: stateViewAddress[chainId as keyof typeof stateViewAddress],
    abi: stateViewAbi,
    functionName: 'getFeeGrowthInside' as const,
    args: [poolIdBytes32, tickLower, tickUpper],
  });

  // Fetch the uncollected fees for the position
  const [currency0UncollectedFees, currency1UncollectedFees] = [
    getPositionUncollectedFees({
      feeGrowthInsideX128: feeGrowthInside0X128,
      feeGrowthInsideLastX128: feeGrowthInside0LastX128,
      liquidity,
    }),
    getPositionUncollectedFees({
      feeGrowthInsideX128: feeGrowthInside1X128,
      feeGrowthInsideLastX128: feeGrowthInside1LastX128,
      liquidity,
    }),
  ];

  const [token0Asset, token1Asset] = [
    assetByAddress(chainId, poolKey.currency0),
    assetByAddress(chainId, poolKey.currency1),
  ];

  if (token0Asset === undefined || token1Asset === undefined) {
    throw new Error(`Token assets for position (${positionTokenId}) are invalid`);
  }

  // Fetch the pool state for a given poolKey to be used to generate the position properties
  const [sqrtPriceX96, tick, protocolFeeNumber] = await readContract(wagmiConfig, {
    address: stateViewAddress[chainId as keyof typeof stateViewAddress],
    abi: stateViewAbi,
    functionName: 'getSlot0',
    args: [poolIdBytes32],
  });

  // Fetch the pool liquidity
  const poolLiquidity = await readContract(wagmiConfig, {
    address: stateViewAddress[chainId as keyof typeof stateViewAddress],
    abi: stateViewAbi,
    functionName: 'getLiquidity',
    args: [poolIdBytes32],
  });

  // Build the pool state from the fetched data
  const poolState = {
    liquidity: poolLiquidity,
    sqrtPriceX96,
    tick,
    protocolFee: BigInt(protocolFeeNumber),
    currentPriceScaled: tickToPriceScaled(tick),
  };

  // getPositionFromPositionInfo positionInfo argument only needs poolKey, liquidity, tickUpper, and tickLower
  const positionInfo = {
    tokenId: positionTokenId,
    poolKey,
    tickLower,
    tickUpper,
    liquidity,
    feeGrowthInside0X128,
    feeGrowthInside1X128,
    feeGrowthInside0LastX128,
    feeGrowthInside1LastX128,
    currency0UncollectedFees,
    currency1UncollectedFees,
  } as PositionInfo;

  // Get the position from the position info
  const position = getPositionFromPositionInfo({
    chainId,
    positionInfo,
    poolState,
  });

  if (position === undefined) {
    throw new Error(`Position (${positionTokenId}) not found`);
  }

  // Get the liquidity amount for the position
  const [liquidity0Amount, liquidity1Amount] = [
    position.amount0.toExact(),
    position.amount1.toExact(),
  ];

  const [uncollectedFees0CurrencyAmount, uncollectedFees1CurrencyAmount] = [
    getCurrencyAmount({
      chainId,
      asset: token0Asset,
      rawAmount: currency0UncollectedFees,
    }),
    getCurrencyAmount({
      chainId,
      asset: token1Asset,
      rawAmount: currency1UncollectedFees,
    }),
  ];

  const [uncollectedFees0Amount, uncollectedFees1Amount] = [
    uncollectedFees0CurrencyAmount?.toExact() ?? '0',
    uncollectedFees1CurrencyAmount?.toExact() ?? '0',
  ];

  // reference: https://github.com/Uniswap/interface/blob/main/apps/web/src/hooks/useIsTickAtLimit.ts#L5
  // Check if the tick is at the limit
  const tickAtLimit = {
    [Bound.LOWER]: tickLower === nearestUsableTick(TickMath.MIN_TICK, poolKey.tickSpacing),
    [Bound.UPPER]: tickUpper === nearestUsableTick(TickMath.MAX_TICK, poolKey.tickSpacing),
  };

  // Check if the tick is in the range between the tickLower and tickUpper (tickLower <= poolState.tick <= tickUpper)
  const tickInRange = tickLower <= poolState.tick && poolState.tick <= tickUpper;

  // Generate the position tokens
  const positionTokens = [
    {
      asset: token0Asset,
      token: new Token(
        chainId,
        token0Asset.address[chainId],
        token0Asset.exponent,
        token0Asset.displaySymbol,
        token0Asset.name,
      ),
      symbol: token0Asset.displaySymbol,
      imageUrl: token0Asset.imageUrl,
      uncollectedFees: currency0UncollectedFees,
      uncollectedFeesCurrencyAmount: uncollectedFees0CurrencyAmount,
      uncollectedFeesAmount: uncollectedFees0Amount,
      liquidityCurrencyAmount: position.amount0,
      liquidityAmount: liquidity0Amount,
    },
    {
      asset: token1Asset,
      token: new Token(
        chainId,
        token1Asset.address[chainId],
        token1Asset.exponent,
        token1Asset.displaySymbol,
        token1Asset.name,
      ),
      symbol: token1Asset.displaySymbol,
      imageUrl: token1Asset.imageUrl,
      uncollectedFees: currency1UncollectedFees,
      uncollectedFeesCurrencyAmount: uncollectedFees1CurrencyAmount,
      uncollectedFeesAmount: uncollectedFees1Amount,
      liquidityCurrencyAmount: position.amount1,
      liquidityAmount: liquidity1Amount,
    },
  ];

  return {
    positionId: positionTokenId,
    poolId: poolIdBytes32,
    poolState,
    feePercent,
    tickLower,
    tickUpper,
    tickAtLimit,
    tickInRange,
    liquidity,
    poolKey,
    position,
    positionInfo,
    positionTokens,
    positionManagerAddress: resolvedPositionManagerAddress,
  };
}

export const QUERY_KEY_POSITION_DETAILS = (
  chainId: number,
  address?: `0x${string}`,
  positionId?: bigint | undefined | null,
  positionManagerAddress?: `0x${string}`,
) => ['usePositionDetails', chainId, address, positionId, positionManagerAddress];

const QUERY_FN_POSITION_DETAILS = async function queryFnPositionDetails<
  T extends bigint | null | undefined,
>(
  chainId: number,
  address: `0x${string}` | undefined,
  positionTokenId?: T,
  positionManagerAddress?: `0x${string}`,
): Promise<T extends bigint ? FetchedPositionDetails : null> {
  // This is a escape hatch for the case where the position token id is undefined or null
  if (positionTokenId === undefined || positionTokenId === null) {
    return null as T extends bigint ? FetchedPositionDetails : null;
  }
  if (!chainId) {
    throw new Error('Chain ID is required');
  }
  if (!address) {
    throw new Error('Account address is required');
  }

  return (await fetchPositionDetails(
    chainId,
    positionTokenId,
    positionManagerAddress,
  )) as T extends bigint ? FetchedPositionDetails : null;
};

export function updatePositionDetailsPricing(
  positionDetails: NonNullable<FetchedPositionDetails>,
  prices: AssetUSDPriceMap<string[]>,
) {
  const positionTokens = positionDetails.positionTokens.map((token) => {
    return {
      ...token,
      priceNotional: prices[token.symbol],
      // Calculate the uncollected fees notional by multiplying the uncollected fees amount by the price
      uncollectedFeesNotional: prices[token.symbol] * Number(token.uncollectedFeesAmount),
      // Calculate the liquidity notional by multiplying the liquidity amount by the price
      liquidityNotional: prices[token.symbol] * Number(token.liquidityAmount),
    };
  });

  // Calculate the total liquidity notional
  const totalLiquidityNotional = positionTokens.reduce(
    (acc, positionToken) => acc + positionToken.liquidityNotional,
    0,
  );

  // Calculate the total unclaimed fees notional
  const totalUnclaimedFeesNotional = positionTokens.reduce(
    (acc, positionToken) => acc + Number(positionToken.uncollectedFeesNotional),
    0,
  );

  return {
    ...positionDetails,
    positionTokens,
    totalLiquidityNotional,
    totalUnclaimedFeesNotional,
  };
}

/**
 * Fetch the position info for a single position
 * @param positionId - The token id of the position to fetch
 * @param positionManagerAddress - Optional position manager address for efficient direct query
 * @returns The position info
 */
export function usePositionDetails<T extends bigint | null | undefined>(
  positionId: T,
  positionManagerAddress?: `0x${string}`,
): T extends bigint ? PositionDetails : null {
  const account = useAccount();

  const chainId = useChainId();

  const queryOptions = useMemo(
    () => ({
      queryKey: QUERY_KEY_POSITION_DETAILS(
        chainId,
        account.address,
        positionId,
        positionManagerAddress,
      ),
      queryFn: async () =>
        QUERY_FN_POSITION_DETAILS(chainId, account.address, positionId, positionManagerAddress),
    }),
    [chainId, account.address, positionId, positionManagerAddress],
  );

  const { data: positionDetails } = useSuspenseQuery(queryOptions);

  // Update position details with pricing data
  // Get token symbols to fetch asset prices
  const tokenSymbols = useMemo(
    () =>
      positionDetails?.positionTokens
        ? positionDetails.positionTokens.map((token) => token.symbol)
        : [],
    [positionDetails?.positionTokens],
  );
  const { data: prices } = useAssetUSDPrices(tokenSymbols);
  const pricedPositionDetails = useMemo(
    () => (positionDetails ? updatePositionDetailsPricing(positionDetails, prices) : null),
    [positionDetails, prices],
  );

  return pricedPositionDetails as T extends bigint ? PositionDetails : null;
}

/**
 * Fetch the position info for multiple positions
 * @param positionIds - The token ids of the positions to fetch
 * @returns The position info for each position
 */
export function usePositionsDetails(positionIds: bigint[]) {
  const account = useAccount();

  const chainId = useChainId();

  const queries = useMemo(
    () =>
      positionIds.map((positionId) => ({
        queryKey: QUERY_KEY_POSITION_DETAILS(chainId, account.address, positionId),
        queryFn: async () => QUERY_FN_POSITION_DETAILS(chainId, account.address, positionId),
      })),
    [account.address, chainId, positionIds],
  );

  const query = useSuspenseQueries({
    queries,
  });

  // Update position details with pricing data
  // Get token symbols to fetch asset prices
  const tokenSymbols = useMemo(
    () =>
      query.flatMap((positionDetails) =>
        positionDetails.data.positionTokens.map((token) => token.symbol),
      ),
    [query],
  );
  const { data: prices } = useAssetUSDPrices(tokenSymbols);
  const positionsDetails = useMemo(
    () =>
      query.map((positionDetails) => updatePositionDetailsPricing(positionDetails.data, prices)),
    [query, prices],
  );

  return positionsDetails;
}
